import React from "react";
import { ReactComponent as RequestIcon } from "icons/request-icon.svg";
import { useRfxPathname } from "app/useRfxPathname";
import {
	scopeMenuCount,
	useRfxMenuCounts,
} from "newui/features/Rfx/hooks/useRfxMenuCounts";
import type { RfxListScope } from "newui/features/Rfx/types/rfx-list.type";
import {
	ExpandableMenuChildItem,
	ExpandableMenuItem,
} from "../ExpandableMenuItem";

export interface RfxMenuLinks {
	approvals: string;
	panel: string;
	tendersQuotes: string;
}

interface AppMenuItemRfxMenuProps {
	isCollapsed: boolean;
	links: RfxMenuLinks;
	total?: number | null;
	onNavigate?: () => void;
}

const STORAGE_KEY = "rfxMenu_expanded";

/**
 * A scope's menu entry stays highlighted on EVERY one of its tabs, not just the
 * `/current` the link points at. The links are full tab paths
 * (`/account/1/rfx/tenders-quotes/current`), so the tab segment is trimmed and
 * the remainder prefix-matched — `…/tenders-quotes/awarded` still lights up
 * Tenders & Quotes.
 *
 * The trailing slash on the prefix matters: without it `/rfx/panel` would also
 * match a hypothetical `/rfx/panel-review`.
 */
const isScopeActive = (pathname: string, link: string): boolean => {
	const scopeBase = link.slice(0, link.lastIndexOf("/"));

	if (!scopeBase) {
		return false;
	}

	return pathname === scopeBase || pathname.startsWith(`${scopeBase}/`);
};

/**
 * Always-visible T&Q / Approvals / Panel submenu (R1/R3) — replaces the old
 * flat single-link `AppMenuItemRfx`.
 *
 * Labels and routes are static. The badges come from one shared, cached
 * `useRfxMenuCounts` query covering all three scopes, so mounting this in both
 * menu variants costs a single request rather than one per scope.
 */
export const AppMenuItemRfxMenu = ({
	links,
	onNavigate,
	total,
	isCollapsed,
}: AppMenuItemRfxMenuProps) => {
	const pathname = useRfxPathname();
	const { counts, mode } = useRfxMenuCounts();

	const entries = [
		{
			name: "Tenders & Quotes",
			link: links.tendersQuotes,
			scope: "tenders_quotes" as RfxListScope,
			testId: "rfx-menu-tenders-quotes",
		},
		{
			name: "Approvals",
			link: links.approvals,
			scope: "approvals" as RfxListScope,
			testId: "rfx-menu-approvals",
		},
		{
			name: "Panel",
			link: links.panel,
			scope: "panel" as RfxListScope,
			testId: "rfx-menu-panel",
		},
	].map((entry) => ({
		...entry,
		count: scopeMenuCount(counts, entry.scope, mode),
	}));

	/**
	 * The parent badge is the sum of the three child badges, so a collapsed menu
	 * summarises exactly what expanding it shows. Procurers previously had NO
	 * top-level number at all: `AppMenuClient` passes no `total`, and the bidder
	 * side only had one because it inherited a legacy redux count
	 * (`supplier_rfq_total`) that the client menu never populated.
	 *
	 * `total` still wins when supplied, so that legacy bidder badge is unchanged.
	 * Deriving it here rather than plumbing a second bespoke prop into
	 * `AppMenuClient` keeps one source of truth: the counts already driving the
	 * children cannot disagree with the parent summarising them.
	 */
	const derivedTotal = entries.reduce((sum, { count }) => sum + count, 0);

	// NOTE: the parent "RFx" row is deliberately NOT highlighted when a child is
	// active — the child's own highlight already locates you, and lighting both
	// reads as two separate selections.
	return (
		<ExpandableMenuItem
			icon={<RequestIcon />}
			label="RFx"
			storageKey={STORAGE_KEY}
			isCollapsed={isCollapsed}
			collapsedLink={links.tendersQuotes}
			testId="rfx-menu"
			tourName="RFx"
			badge={total ?? derivedTotal}
			onNavigate={onNavigate}
		>
			{entries.map(({ name, link, scope, testId, count }) => (
				<ExpandableMenuChildItem
					key={scope}
					name={name}
					link={link}
					count={count}
					isActive={isScopeActive(pathname, link)}
					testId={testId}
					onNavigate={onNavigate}
				/>
			))}
		</ExpandableMenuItem>
	);
};
